import * as d3 from 'd3';
import $ from 'jquery';
import simplify from 'simplify-js';
import {debounce} from 'ts-debounce';
import _ from 'underscore';

import GapSequenceWrapper from 'analyses/alignment/GapSequenceWrapper';
import {TraceData} from 'analyses/Analyses';
import {assert} from 'Asserts';
import {Sequence} from 'components/Sequence';
import {ALIGNMENT_SCROLLING_PERF_INTERVAL} from 'constants/AlignmentConstants';
import Component, {ComponentOptions} from 'molbio/maps/linear/Component';
import {Trace as TraceLayout} from 'molbio/maps/linear/LinearLayout';
import Slice from 'molbio/maps/linear/Slice';
import Range from 'molbio/maps/selectables/Range';
import {TabState} from 'Navigation';
import * as Perf from 'performance/Perf';
import {PerfId} from 'performance/Perf';
import * as vo from 'settings/visibility-options/VisibilityOptions';
import {VisibilityOption} from 'settings/visibility-options/VisibilityOptions';
import addTestAttrsToD3Selection from 'test/lib/cypress/addTestAttrsToD3Selection';
import * as logging from 'util/Logging';

type TraceDatum = [string, Array<Array<number>>];

export interface TraceViewOptions extends ComponentOptions {
  data?: TraceData | null;
  range: Range | null;
  slice: Slice;
}

class TraceView extends Component {
  data?: TraceData | null;
  layout!: TraceLayout;
  performanceId!: PerfId;
  range!: Range | null;
  sequence!: Sequence;
  slice!: Slice;
  tabstate!: TabState;

  static override initClass(): void {
    this.prototype.className = 'trace';
    this.prototype.setting = vo.TRACE.name;
  }

  override initialize(options: TraceViewOptions): void {
    super.initialize(options);
    this.performanceId = Perf.start(
      'drawAlignmentTrace',
      {enabled: false},
      {
        interval: ALIGNMENT_SCROLLING_PERF_INTERVAL,
        aggregations: [Perf.Aggregation.MEDIAN, Perf.Aggregation.MAX],
      },
      {}
    );
    this.data = options.data;
    this.tabstate = options.tabstate;
    this.slice = options.slice;
    this.range = options.range;
    this.layout = this.model.get('layout').Trace;
    this.sequence = this.model.get('sequence');
    assert(this.sequence instanceof GapSequenceWrapper, 'Trace view must take a gap sequence wrapper!');
  }

  qualityOption(): VisibilityOption {
    return this.model.get('visibilityOptions').getOption(vo.TRACE_QUALITY.name);
  }

  override showHandler(): void {
    super.showHandler();
    this.model.trigger('traceToggle');
  }

  _getIndexToBases(
    colorData: Array<Array<number>>,
    start: number,
    end: number,
    xTransform: (x: number) => number,
    yTransform: (y: number) => number
  ): Array<[number, number] | null> {
    const path = [];
    let didWarn = false;
    let points: Array<number> = [];
    for (let base = start; base < end; base++) {
      points = colorData[base];
      if (points == null) {
        // Sometimes after a realignment, we rerender the new sequence before the slice end is updated,
        // likely due to a backbone listeners firing. The hypothesis is that this warning only happens
        // when sliceEnd > sequenceLength (they should always be equal for alignments right now),
        // so we only warn if that sliceEnd <= sequenceLength. Ref T1356.
        if (!didWarn && this.slice.end() <= this.sequence.length()) {
          didWarn = true;
          // TODO(T1356) Unexpected, but we warn with extra data and fall back to empty array.
          // Use soaked accessors just to be defensive, in case we're in a weird state.
          logging.warn('T1356: colorData[base] is undefined, falling back to []', {
            extra: {
              base,
              start,
              end,
              colorDataLength: colorData.length,
              sliceStart: this.slice != null ? this.slice.start() : undefined,
              sliceEnd: this.slice != null ? this.slice.end() : undefined,
              sequenceLength: this.sequence != null ? this.sequence.length() : undefined,
            },
          });
        }
        points = [];
      }
      if (points.length === 0) {
        path.push(null);
      } else {
        // oxlint-disable-next-line no-loop-func -- this appears to be a false positie, as base is defined with let
        const dicts = points.map((point, i) => ({
          x: xTransform(base + i / points.length),
          y: yTransform(point),
        }));
        const simplified = simplify(dicts, 1, false);
        path.push(...simplified.map<[number, number]>((p) => [p.x, p.y]));
      }
    }
    return path;
  }

  _getDataString(
    points: Array<Array<number>>,
    xTransform: (x: number) => number,
    yTransform: (y: number) => number
  ): string {
    const n = points.length;
    const path = [];
    let type = 'M';
    let index = 0;
    while (index < n) {
      if (points[index]) {
        path.push(type, xTransform(points[index][0]), ',', yTransform(points[index][1]), ' ');
        type = 'L';
      } else {
        type = 'M';
      }
      index++;
    }
    return path.join('');
  }

  drawHoverBoxes(): void {
    Perf.markStart(this.performanceId);

    // From the slice we determine where the boxes should go.
    const start = Math.floor(this.range!.start());
    const end = Math.floor(this.range!.end());

    const max = this.layout.get('maxQuality');
    const yMax = this.layout.height();

    // Do not draw when the height or quality is NaN or <= 0
    if (!(yMax > 0) || !(max > 0)) {
      Perf.markEnd(this.performanceId);
      return;
    }

    const y = d3.scaleLinear().domain([0, max]).range([0, yMax]);
    const x = this.slice.x();

    const qualityScores = this.qualityOption().get('visible') ? this.data!.get('qualityScores') : [];
    const hoverBar = d3
      .select<SVGElement, [number, number]>(this.el)
      .select<SVGGElement>('g')
      .select<SVGGElement>('g.quality-hover-bars')
      .selectAll<SVGRectElement, [number, number]>('rect')
      .data(
        _.chain(qualityScores)
          .map((q, i) => [q, i])
          .filter((d, i) => d[0] !== 0 && start <= i && i < end)
          .value()
      );

    const {layout} = this;
    hoverBar
      .join<SVGRectElement>('svg:rect')
      .classed('hoverBar', true)
      .each(function () {
        return $(this).tooltip({
          // @ts-expect-error We're kicking this off from a jQuery, but bootstrap clobbers jQuery.tooltip
          delay: {show: 2000, hide: 0},
          placement: 'top',
          container: 'body',
          trigger: 'manual',
          animate: false,
        });
      })
      .attr('x', (d: [number, number]) => x(d[1]))
      .attr('width', x(1))
      .attr('y', (d: [number, number]) => yMax - y(d[0]))
      .attr('height', (d: [number, number]) => y(d[0]))
      // @ts-expect-error I think we have some d3 type skew. d3 can accept a d3.RGBColor for svg fill, it doesn't need a string
      .attr('fill', d3.rgb(layout.get('qualityFillColor')).darker())
      .attr('fill-opacity', 0.0)
      .on('mouseenter', function () {
        d3.select(this).attr('fill-opacity', layout.get('qualityFillOpacity'));
        // FIXME: Why didn't trigger: hover, work? also fix in voteview.js
        clearTimeout($(this).data('showIntervalId'));
        return $(this).data(
          'showIntervalId',
          setTimeout(
            // eslint-disable-next-line no-restricted-syntax
            _.bind(function () {
              // @ts-expect-error shadows outer this
              return $(this).tooltip('show');
            }, this),
            300
          )
        );
      })
      .on('mouseleave', function () {
        clearTimeout($(this).data('showIntervalId'));
        $(this).data('showIntervalId', null);
        $(this).tooltip('hide');
        return d3.select(this).attr('fill-opacity', 0.0);
      })
      .on(
        'click',
        // eslint-disable-next-line no-restricted-syntax
        _.bind(function (e: MouseEvent, d: [number, number]) {
          // @ts-expect-error shadows outer this
          this.model
            .get('sequence')
            .get('selection')
            .setEndpoints(d[1], d[1] + 1);
          e.stopPropagation();
        }, this)
      )
      .each(function (d) {
        $(this).attr('data-original-title', `Quality: ${d[0]}`);
        return $(this).tooltip('fixTitle');
      });

    hoverBar.exit().each(function () {
      $(this).tooltip('destroy');
    });

    Perf.markEnd(this.performanceId);
  }

  override update(trigger?: boolean): void {
    if (trigger == null) {
      trigger = true;
    }
    if (!this.shouldShow()) {
      return;
    }

    const start = this.slice.start();
    const end = this.slice.end();

    const id = (f: number): number => f;
    const x = this.slice.x();
    let y = d3
      .scaleLinear()
      .domain([0, this.layout.get('maxQuality')])
      .range([this.layout.height(), 0]);
    const quality = !this.qualityOption().get('visible')
      ? []
      : _.chain(this.data!.get('qualityScores').slice(start, end))
          .map((q, i) => {
            const j = i + start;
            return q
              ? [
                  [j, 0],
                  [j, q],
                  [j + 1, q],
                  [j + 1, 0],
                ]
              : null;
          })
          .flatten(true)
          .value();
    const qualityData = this._getDataString(quality, x, y);
    const root = d3
      .select(this.el)
      .select('g')
      .select('g.quality-bars')
      .selectAll('path')
      .data([qualityData]);
    root
      .join('svg:path')
      .attr('d', (d) => d)
      .attr('stroke', this.layout.get('qualityStrokeColor'))
      .attr('fill', this.layout.get('qualityFillColor'))
      .attr('fill-opacity', this.layout.get('qualityFillOpacity'))
      .attr('stroke-opacity', this.layout.get('qualityStrokeOpacity'));
    // .attr('shape-rendering', 'optimizeSpeed')

    const color = d3
      .select(this.el)
      .select('g')
      .select('g.color-data')
      .selectAll('path')
      .data(_.pairs(this.data!.get('baseToColor')));

    const maxY = this.data!.get('maxY');
    y = d3.scaleLinear().domain([0, maxY]).range([this.layout.height(), 0]).clamp(true);
    // eslint-disable-next-line no-restricted-syntax
    const getData: (d: Array<Array<number>>) => string = _.bind(function (d: Array<Array<number>>) {
      // @ts-expect-error shadows outer this
      return this._getDataString(this._getIndexToBases(d, start, end, x, y), id, id);
    }, this);

    color
      .join('svg:path')
      .attr('d', (d: TraceDatum) => getData(d[1]))
      .attr(
        'stroke',
        // eslint-disable-next-line no-restricted-syntax
        _.bind(function (d: TraceDatum) {
          // @ts-expect-error shadows outer this
          return this.layout.get('colorMap')[d[0]];
        }, this)
      )
      .attr('stroke-width', this.layout.get('colorStrokeWidth'))
      .attr('fill', 'none');
    // .attr('shape-rendering', 'optimizeSpeed')

    this.drawHoverBoxes();
    if (trigger) {
      return this.trigger('redraw');
    }
  }

  override setup(): void {
    super.setup();
    const root = d3.select(this.el).select('g');
    root.append('svg:g').attr('class', 'quality-bars');
    const hoverBars = root.append('svg:g').attr('class', 'quality-hover-bars');
    // @ts-expect-error I think `append` does not return the same kind of `d3.Selection` as `d3.select`
    // this still works and adds the test attrs though
    addTestAttrsToD3Selection(hoverBars, {component: 'TraceView', element: 'qualityHoverBars'});
    root.append('svg:g').attr('class', 'color-data');
  }

  override registerListeners(): void {
    super.registerListeners();
    // eslint-disable-next-line no-restricted-syntax, @typescript-eslint/unbound-method
    this.listenTo(this.range, 'change:start change:end', debounce(_.bind(this.drawHoverBoxes, this), 500));
    this.listenTo(this.data, 'change:maxY', () => {
      this.update();
    });
    this.listenTo(this.qualityOption(), 'change:visible', () => {
      this.update();
    });
  }
}
TraceView.initClass();

export default TraceView;
